import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Check, XCircle, Edit3, Clock, DollarSign, BookOpen, Users, Trophy, MessageSquare, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { ChallengeRequestData } from '../../types/challenge.types';

interface EditedChallengeReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  challengeData: {
    id: string;
    originalData: ChallengeRequestData;
    editedData: ChallengeRequestData;
    editedBy: string;
    editedAt: string;
  };
  onChallengeAccepted: (data: ChallengeRequestData) => void;
  onChallengeRejected: (reason: string) => void;
}

interface Subject {
  id: string;
  name: string;
  topics: string[];
}

const subjects: Subject[] = [
  {
    id: 'mathematics',
    name: 'Mathematics',
    topics: ['Algebra', 'Geometry', 'Calculus', 'Statistics', 'Trigonometry', 'Arithmetic']
  },
  {
    id: 'science',
    name: 'Science',
    topics: ['Physics', 'Chemistry', 'Biology', 'Earth Science', 'Astronomy']
  },
  {
    id: 'english',
    name: 'English',
    topics: ['Grammar', 'Literature', 'Composition', 'Vocabulary', 'Reading Comprehension']
  },
  {
    id: 'history',
    name: 'History',
    topics: ['World History', 'Ancient Civilizations', 'Modern Era', 'Geography', 'Political Science']
  },
  {
    id: 'general',
    name: 'General Knowledge',
    topics: ['Current Events', 'Pop Culture', 'Technology', 'Sports', 'Arts']
  }
];

const EditedChallengeReviewModal: React.FC<EditedChallengeReviewModalProps> = ({
  isOpen,
  onClose,
  challengeData,
  onChallengeAccepted,
  onChallengeRejected
}) => {
  const { user: authUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const rejectReasons = [
    'Stake amount too high',
    'Stake amount too low',
    'Time not suitable',
    'Topic not interested',
    'Too many questions',
    'Too few questions',
    'Other changes not acceptable',
    'Other'
  ];

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : subjectId;
  };

  const getTopicName = (subjectId: string, topic: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (subject && subject.topics.includes(topic)) {
      return topic;
    }
    return topic;
  };

  const formatStake = (stake: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(stake);
  };

  const formatTime = (time: string) => {
    if (!time) return 'Not specified';
    return new Date(time).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration: number) => {
    if (duration < 60) {
      return `${duration} minutes`;
    }
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
  };

  const handleAcceptChallenge = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request/accept-edited', {
        challengeId: challengeData.id
      });

      if (response.data?.success) {
        toast.success('Challenge accepted! 🎉');
        onChallengeAccepted(challengeData.editedData);
        onClose();
      } else {
        throw new Error('Failed to accept challenge');
      }
    } catch (error) {
      console.error('Error accepting challenge:', error);
      toast.error('Failed to accept challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectChallenge = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    if (!rejectReason.trim()) {
      toast.error('Please select a reason for rejection');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/competitors/challenge-request/reject-edited', {
        challengeId: challengeData.id,
        reason: rejectReason
      });

      if (response.data?.success) {
        toast.success('Challenge rejected');
        onChallengeRejected(rejectReason);
        onClose();
      } else {
        throw new Error('Failed to reject challenge');
      }
    } catch (error) {
      console.error('Error rejecting challenge:', error);
      toast.error('Failed to reject challenge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => {
    const original = challengeData.originalData;
    const edited = challengeData.editedData;
    
    return (
      original.stake !== edited.stake ||
      original.numberOfQuestions !== edited.numberOfQuestions ||
      original.startTime !== edited.startTime ||
      original.duration !== edited.duration ||
      original.mode !== edited.mode ||
      original.message !== edited.message
    );
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <HeaderTitle>
            <Edit3 size={24} />
            Challenge Request Edited
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <AlertSection>
            <AlertCircle size={20} color="#f59e0b" />
            <AlertText>
              <strong>{challengeData.editedBy}</strong> has edited your challenge request and sent it back to you.
            </AlertText>
          </AlertSection>

          <SectionTitle>Review Changes</SectionTitle>
          
          {hasChanges() ? (
            <ChangesSection>
              <ChangeItem>
                <ChangeLabel>Subject & Topic:</ChangeLabel>
                <ChangeValue>
                  {getSubjectName(challengeData.editedData.subject)} - {getTopicName(challengeData.editedData.subject, challengeData.editedData.topic)}
                </ChangeValue>
              </ChangeItem>

              <ChangeItem>
                <ChangeLabel>Stake Amount:</ChangeLabel>
                <ChangeValue>
                  {formatStake(challengeData.editedData.stake)}
                  {challengeData.originalData.stake !== challengeData.editedData.stake && (
                    <ChangeIndicator>
                      {challengeData.originalData.stake > challengeData.editedData.stake ? '↓' : '↑'}
                    </ChangeIndicator>
                  )}
                </ChangeValue>
              </ChangeItem>

              <ChangeItem>
                <ChangeLabel>Number of Questions:</ChangeLabel>
                <ChangeValue>
                  {challengeData.editedData.numberOfQuestions}
                  {challengeData.originalData.numberOfQuestions !== challengeData.editedData.numberOfQuestions && (
                    <ChangeIndicator>
                      {challengeData.originalData.numberOfQuestions > challengeData.editedData.numberOfQuestions ? '↓' : '↑'}
                    </ChangeIndicator>
                  )}
                </ChangeValue>
              </ChangeItem>

              <ChangeItem>
                <ChangeLabel>Start Time:</ChangeLabel>
                <ChangeValue>
                  {formatTime(challengeData.editedData.startTime)}
                  {challengeData.originalData.startTime !== challengeData.editedData.startTime && (
                    <ChangeIndicator>🕐</ChangeIndicator>
                  )}
                </ChangeValue>
              </ChangeItem>

              <ChangeItem>
                <ChangeLabel>Duration:</ChangeLabel>
                <ChangeValue>
                  {formatDuration(challengeData.editedData.duration)}
                  {challengeData.originalData.duration !== challengeData.editedData.duration && (
                    <ChangeIndicator>⏱️</ChangeIndicator>
                  )}
                </ChangeValue>
              </ChangeItem>

              <ChangeItem>
                <ChangeLabel>Mode:</ChangeLabel>
                <ChangeValue>
                  {challengeData.editedData.mode === 'live' ? 'Live Competition' : 'Timed Competition'}
                  {challengeData.originalData.mode !== challengeData.editedData.mode && (
                    <ChangeIndicator>🔄</ChangeIndicator>
                  )}
                </ChangeValue>
              </ChangeItem>

              {challengeData.editedData.message && (
                <ChangeItem>
                  <ChangeLabel>Message:</ChangeLabel>
                  <ChangeValue>{challengeData.editedData.message}</ChangeValue>
                </ChangeItem>
              )}
            </ChangesSection>
          ) : (
            <NoChangesMessage>
              No changes were made to the challenge request.
            </NoChangesMessage>
          )}

          <EditInfo>
            <EditIcon size={16} />
            <span>Edited by {challengeData.editedBy} on {new Date(challengeData.editedAt).toLocaleDateString()}</span>
          </EditInfo>
        </ModalContent>

        <ModalActions>
          <RejectButton 
            onClick={() => setShowRejectModal(true)}
            disabled={loading}
          >
            <XCircle size={20} />
            Reject Edit
          </RejectButton>
          
          <AcceptButton 
            onClick={handleAcceptChallenge}
            disabled={loading}
          >
            <Check size={20} />
            Accept Document
          </AcceptButton>
        </ModalActions>

        {/* Reject Reason Modal */}
        {showRejectModal && (
          <RejectModalOverlay>
            <RejectModal>
              <RejectModalHeader>
                <h3>Select Rejection Reason</h3>
                <CloseButton onClick={() => setShowRejectModal(false)}>
                  <X size={20} />
                </CloseButton>
              </RejectModalHeader>
              
              <RejectModalContent>
                <RejectReasonList>
                  {rejectReasons.map((reason) => (
                    <RejectReasonItem
                      key={reason}
                      onClick={() => setRejectReason(reason)}
                      selected={rejectReason === reason}
                    >
                      {reason}
                    </RejectReasonItem>
                  ))}
                </RejectReasonList>
                
                {rejectReason === 'Other' && (
                  <CustomReasonInput
                    placeholder="Please specify your reason..."
                    value={rejectReason === 'Other' ? rejectReason : ''}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                )}
              </RejectModalContent>
              
              <RejectModalActions>
                <CancelButton onClick={() => setShowRejectModal(false)}>
                  Cancel
                </CancelButton>
                <ConfirmRejectButton 
                  onClick={handleRejectChallenge}
                  disabled={!rejectReason.trim() || loading}
                >
                  Confirm Rejection
                </ConfirmRejectButton>
              </RejectModalActions>
            </RejectModal>
          </RejectModalOverlay>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
`;

const AlertSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const AlertText = styled.p`
  margin: 0;
  color: #92400e;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
`;

const ChangesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const ChangeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ChangeLabel = styled.span`
  font-weight: 500;
  color: #374151;
`;

const ChangeValue = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1f2937;
  font-weight: 500;
`;

const ChangeIndicator = styled.span`
  font-size: 1.2rem;
  color: #059669;
`;

const NoChangesMessage = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
  font-style: italic;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const EditInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  color: #0369a1;
  font-size: 0.9rem;
`;

const EditIcon = styled(Edit3)`
  color: #0ea5e9;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const RejectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 2px solid #ef4444;
  color: #ef4444;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover:not(:disabled) {
    background: #ef4444;
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AcceptButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #059669;
  border: 2px solid #059669;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover:not(:disabled) {
    background: #047857;
    border-color: #047857;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Reject Modal Styles
const RejectModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const RejectModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
`;

const RejectModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

const RejectModalContent = styled.div`
  padding: 24px;
  max-height: 50vh;
  overflow-y: auto;
`;

const RejectReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const RejectReasonItem = styled.div<{ selected: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.selected ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#fef2f2' : 'white'};
  color: ${props => props.selected ? '#dc2626' : '#374151'};
  font-weight: ${props => props.selected ? '600' : '500'};

  &:hover {
    border-color: #ef4444;
    background: #fef2f2;
  }
`;

const CustomReasonInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const RejectModalActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  border: 2px solid #6b7280;
  color: #6b7280;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover {
    background: #6b7280;
    color: white;
  }
`;

const ConfirmRejectButton = styled.button`
  padding: 12px 24px;
  background: #ef4444;
  border: 2px solid #ef4444;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  &:hover:not(:disabled) {
    background: #dc2626;
    border-color: #dc2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default EditedChallengeReviewModal;
